import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import PlantafelService, { PlantafelData, LaneGroup, LaneTeam, OperationsSettings, WorkAreaConflict, MovedShift, BacklogMeta, BacklogPagination } from '../services/PlantafelService';
import { Shift } from '../../shifts/services/ShiftService';
import { CrmTask } from '../../sales/services/CrmTaskService';
import { pdebug, pdebugWarn, pdebugError } from '../utils/plantafelDebug';

export type ViewMode = 'week' | 'month' | 'year' | 'custom';

export interface PendingSchedule {
  taskId: number;
  teamId: number;
  startDate: string;
  taskTitle: string;
  teamName: string;
}

export interface ConflictState {
  isOpen: boolean;
  conflicts: WorkAreaConflict[];
  pendingSchedule: PendingSchedule | null;
}

interface UsePlantafelReturn {
  loading: boolean;
  error: string | null;
  laneGroups: LaneGroup[];
  shifts: Shift[];
  backlogTasks: CrmTask[];
  backlogMeta: BacklogMeta | null;
  backlogPagination: BacklogPagination | null;
  loadMoreBacklogTasks: () => Promise<void>;
  loadingMoreBacklog: boolean;
  weekStart: Date;
  days: { date: Date; dateStr: string; label: string }[];
  settings: OperationsSettings;
  selectedShift: Shift | null;
  selectedTask: CrmTask | null;
  sidePanelOpen: boolean;
  showJobsOnly: boolean;
  setShowJobsOnly: (value: boolean) => void;
  setWeekStart: (date: Date) => void;
  goToPreviousWeek: () => void;
  goToNextWeek: () => void;
  goToCurrentWeek: () => void;
  refresh: () => Promise<void>;
  handleShiftClick: (shift: Shift, task?: CrmTask | null) => void;
  handleTaskClick: (task: CrmTask) => void;
  closeSidePanel: () => void;
  scheduleBacklogTask: (taskId: number, teamId: number, startDate: string, taskTitle?: string, teamName?: string) => Promise<void>;
  moveShift: (shiftId: number, dateStr: string, workAreaId: number) => Promise<void>;
  moveShiftByDays: (shiftId: number, deltaDays: number, newWorkAreaId?: number) => Promise<void>;
  moveShiftByMinutes: (shiftId: number, deltaMinutes: number, newWorkAreaId?: number) => Promise<void>;
  moveShiftToTime: (shiftId: number, newStartDateTime: string, newWorkAreaId?: number) => Promise<void>;
  resizeShift: (shiftId: number, newEndDateTime: string) => Promise<void>;
  deleteShift: (shiftId: number) => Promise<void>;
  rescheduleShift: (shiftId: number, newTeamId: number, teamName?: string) => Promise<void>;
  getShiftsForCell: (dateStr: string, workAreaId: number) => Shift[];
  hasTeams: boolean;
  conflictState: ConflictState;
  closeConflictModal: () => void;
  pushConflicts: () => Promise<MovedShift[]>;
  showRegularShifts: boolean;
  setShowRegularShifts: (value: boolean) => void;
  // FE-HF-R18-3: Optimistic UI support
  updateShiftOptimistically: (shiftId: number, updater: (shift: Shift) => Shift) => void;
  // FE-OPS-R21-4: View Mode Support
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  viewDays: number;
  goToPreviousPeriod: () => void;
  goToNextPeriod: () => void;
  goToCurrentPeriod: () => void;
  customDateRange: { start: Date; end: Date } | null;
  setCustomDateRange: (range: { start: Date; end: Date } | null) => void;
  // FE-OPS-R22-1: Anchor date for display purposes (the reference date, not rangeFrom)
  anchorDate: Date;
  // FE-OPS-R23-2/3: Set anchor date for navigation (month/year view clicks)
  setAnchorDate: (date: Date) => void;
}

// ============================================================================
// FE-OPS-R22-1: Centralized Date Range Calculation
// ============================================================================

/**
 * Format date as YYYY-MM-DD string
 */
function formatDateStr(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get start of ISO week (Monday) for a given date
 */
function startOfISOWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  // ISO week starts on Monday (1), Sunday is 0
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get end of ISO week (Sunday) for a given date
 */
function endOfISOWeek(date: Date): Date {
  const monday = startOfISOWeek(date);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return sunday;
}

/**
 * Get ISO week number for a date
 * ISO 8601: Week 1 is the week containing the first Thursday of the year
 */
export function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Set to nearest Thursday: current date + 4 - current day number (Monday = 1, Sunday = 7)
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  // Calculate full weeks to nearest Thursday
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return weekNo;
}

/**
 * Get start of month (1st day)
 */
function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

/**
 * Get end of month (last day)
 */
function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

/**
 * Calculate difference in days between two dates
 */
function diffDays(from: Date, to: Date): number {
  const fromTime = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const toTime = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((toTime - fromTime) / (1000 * 60 * 60 * 24));
}

export interface DateRange {
  rangeFrom: Date;
  rangeTo: Date;
  days: number;
}

/**
 * Get week range: Monday to Sunday (ISO week)
 * Example: 2025-12-29 → KW 1, from 2025-12-29 (Mo) to 2026-01-04 (So), 7 days
 */
export function getWeekRange(anchorDate: Date): DateRange {
  const rangeFrom = startOfISOWeek(anchorDate);
  const rangeTo = endOfISOWeek(anchorDate);
  return {
    rangeFrom,
    rangeTo,
    days: 7,
  };
}

/**
 * Get month grid range: Full calendar grid from Monday before month start to Sunday after month end
 * Example: December 2025 → from Mo 01.12.2025 to So 04.01.2026, 35 days
 * Max 42 days (6 weeks)
 */
export function getMonthGridRange(anchorDate: Date): DateRange {
  const monthStart = startOfMonth(anchorDate);
  const monthEnd = endOfMonth(anchorDate);

  // Start from Monday of the week containing the 1st
  const rangeFrom = startOfISOWeek(monthStart);

  // End on Sunday of the week containing the last day
  const rangeTo = endOfISOWeek(monthEnd);

  // Calculate days (max 42 = 6 weeks)
  const days = Math.min(diffDays(rangeFrom, rangeTo) + 1, 42);

  return {
    rangeFrom,
    rangeTo,
    days,
  };
}

// Legacy alias for backward compatibility
function getMonday(date: Date): Date {
  return startOfISOWeek(date);
}

const DEFAULT_SETTINGS: OperationsSettings = {
  day_start: '06:00',
  day_end: '18:00',
  day_start_minutes: 6 * 60,
  day_end_minutes: 18 * 60,
  snap_minutes: 30,
  default_shift_duration_hours: 8,
};

export default function usePlantafel(): UsePlantafelReturn {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [laneGroups, setLaneGroups] = useState<LaneGroup[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [backlogTasks, setBacklogTasks] = useState<CrmTask[]>([]);
  const [backlogMeta, setBacklogMeta] = useState<BacklogMeta | null>(null);
  const [backlogPagination, setBacklogPagination] = useState<BacklogPagination | null>(null);
  const [loadingMoreBacklog, setLoadingMoreBacklog] = useState(false);
  const [settings, setSettings] = useState<OperationsSettings>(DEFAULT_SETTINGS);
  // anchorDate: The reference date for the current view (used to calculate ranges)
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [selectedTask, setSelectedTask] = useState<CrmTask | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [showJobsOnly, setShowJobsOnly] = useState(true); // Default: only show job shifts
  const [showRegularShifts, setShowRegularShifts] = useState(true); // Default: load all shifts (like Shiftplan)

  // FE-OPS-R21-4: View Mode Support
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [customDateRange, setCustomDateRange] = useState<{ start: Date; end: Date } | null>(null);

  // FE-OPS-R22-1: Calculate date range based on view mode
  const dateRange = useMemo((): DateRange => {
    switch (viewMode) {
      case 'week':
        return getWeekRange(anchorDate);
      case 'month': {
        // Month view: first to last day of the month (no calendar grid padding)
        const year = anchorDate.getFullYear();
        const month = anchorDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        return {
          rangeFrom: firstDay,
          rangeTo: lastDay,
          days: lastDay.getDate(),
        };
      }
      case 'year':
        // Year view: entire year, but we only load projects (no shifts)
        return {
          rangeFrom: new Date(anchorDate.getFullYear(), 0, 1),
          rangeTo: new Date(anchorDate.getFullYear(), 11, 31),
          days: 365,
        };
      case 'custom':
        if (customDateRange) {
          return {
            rangeFrom: customDateRange.start,
            rangeTo: customDateRange.end,
            days: Math.min(diffDays(customDateRange.start, customDateRange.end) + 1, 42),
          };
        }
        return getWeekRange(anchorDate);
      default:
        return getWeekRange(anchorDate);
    }
  }, [viewMode, anchorDate, customDateRange]);

  // Alias for backward compatibility
  const weekStart = dateRange.rangeFrom;
  const viewDays = dateRange.days;

  // Conflict modal state for 409 handling
  const [conflictState, setConflictState] = useState<ConflictState>({
    isOpen: false,
    conflicts: [],
    pendingSchedule: null,
  });

  // Calculate days array based on view mode
  const days = useMemo(() => {
    if (viewMode === 'year') {
      // Year view: 52/53 weeks as columns
      const year = anchorDate.getFullYear();
      const weeks: { date: Date; dateStr: string; label: string; weekNum: number; month: number }[] = [];

      // Start from first Monday of the year (or last Monday of previous year)
      const jan1 = new Date(year, 0, 1);
      const dayOfWeek = jan1.getDay();
      const firstMonday = new Date(jan1);
      // Move to Monday of the week containing Jan 1
      firstMonday.setDate(jan1.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

      // Generate all weeks until we pass Dec 31
      const dec31 = new Date(year, 11, 31);
      let currentMonday = new Date(firstMonday);
      let weekNum = 1;

      while (currentMonday <= dec31 || weekNum <= 52) {
        const month = currentMonday.getMonth();
        weeks.push({
          date: new Date(currentMonday),
          dateStr: formatDateStr(currentMonday),
          label: `${weekNum}`,
          weekNum,
          month,
        });
        currentMonday.setDate(currentMonday.getDate() + 7);
        weekNum++;
        if (weekNum > 53) break; // Safety limit
      }

      return weeks;
    }

    if (viewMode === 'month') {
      // Month view: actual days in the month (1-28/29/30/31)
      const year = anchorDate.getFullYear();
      const month = anchorDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

      return Array.from({ length: daysInMonth }, (_, i) => {
        const date = new Date(year, month, i + 1);
        const dayNum = (i + 1).toString();
        const dayName = dayNames[date.getDay()];
        return {
          date,
          dateStr: formatDateStr(date),
          label: dayNum,
          dayName,
          weekNum: getISOWeek(date),
        };
      });
    }

    // Week/Custom: generate day columns from rangeFrom
    return PlantafelService.getDayColumns(dateRange.rangeFrom, dateRange.days);
  }, [anchorDate, viewMode, dateRange]);

  // Check if there are any teams available for dropping
  const hasTeams = laneGroups.some(group => group.teams.length > 0);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // FE-OPS-R22-1: Use dateRange for API calls
    const rangeFromStr = formatDateStr(dateRange.rangeFrom);
    const rangeToStr = formatDateStr(dateRange.rangeTo);

    pdebug('loadData:', {
      viewMode,
      rangeFrom: rangeFromStr,
      rangeTo: rangeToStr,
      days: dateRange.days,
    });

    try {
      // All views now load shifts (including year view)
      const data: PlantafelData = await PlantafelService.fetchPlantafelData(
        rangeFromStr,
        showRegularShifts,
        true, // includeBacklogMeta
        dateRange.days // pass the number of days to fetch
      );
      pdebug('loadData result:', {
        viewMode,
        shifts: data.shifts.length,
        laneGroups: data.laneGroups.length,
        backlogTasks: data.backlogTasks.length,
        days: dateRange.days,
      });
      setLaneGroups(data.laneGroups);
      setShifts(data.shifts);
      setBacklogTasks(data.backlogTasks);
      setBacklogMeta(data.backlogMeta);
      setBacklogPagination(data.backlogPagination);
      setSettings(data.settings);
    } catch (err: any) {
      pdebugError('Failed to load plantafel data:', err);
      const errorMsg = err?.message || 'Fehler beim Laden der Plantafel';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [dateRange, showRegularShifts, viewMode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadMoreBacklogTasks = useCallback(async () => {
    if (!backlogPagination?.has_more || loadingMoreBacklog) return;

    setLoadingMoreBacklog(true);
    try {
      const startDate = new Date(weekStart);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
      const weekEnd = endDate.toISOString().split('T')[0];

      const response = await PlantafelService.fetchBacklogTasks(
        formatDateStr(weekStart),
        weekEnd,
        true,
        backlogPagination.limit,
        backlogPagination.offset + backlogPagination.limit
      );

      setBacklogTasks(prev => [...prev, ...response.tasks]);
      setBacklogPagination(response.pagination);
      pdebug('loadMoreBacklogTasks: loaded', response.tasks.length, 'more tasks');
    } catch (err: any) {
      pdebugError('Failed to load more backlog tasks:', err);
      toast.error('Fehler beim Laden weiterer Jobs');
    } finally {
      setLoadingMoreBacklog(false);
    }
  }, [weekStart, backlogPagination, loadingMoreBacklog]);

  // Update selectedShift when shifts array changes (e.g., after refresh)
  // This ensures the side panel shows updated data after employee/asset removal
  useEffect(() => {
    setSelectedShift(prev => {
      if (!prev || shifts.length === 0) return prev;
      const updatedShift = shifts.find(s => s.id === prev.id);
      // Return updated shift if found, otherwise keep previous (shift might be deleted)
      return updatedShift || prev;
    });
  }, [shifts]);

  // Legacy week navigation (for backward compatibility)
  const goToPreviousWeek = useCallback(() => {
    setAnchorDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  }, []);

  const goToNextWeek = useCallback(() => {
    setAnchorDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  }, []);

  const goToCurrentWeek = useCallback(() => {
    setAnchorDate(new Date());
  }, []);

  // FE-OPS-R22-1: Period navigation for different view modes
  const goToPreviousPeriod = useCallback(() => {
    setAnchorDate((prev) => {
      const newDate = new Date(prev);
      switch (viewMode) {
        case 'week':
          newDate.setDate(newDate.getDate() - 7);
          break;
        case 'month':
          newDate.setMonth(newDate.getMonth() - 1);
          break;
        case 'year':
          newDate.setFullYear(newDate.getFullYear() - 1);
          break;
        case 'custom':
          if (customDateRange) {
            const rangeDays = diffDays(customDateRange.start, customDateRange.end) + 1;
            newDate.setDate(newDate.getDate() - rangeDays);
          } else {
            newDate.setDate(newDate.getDate() - 7);
          }
          break;
      }
      return newDate;
    });
  }, [viewMode, customDateRange]);

  const goToNextPeriod = useCallback(() => {
    setAnchorDate((prev) => {
      const newDate = new Date(prev);
      switch (viewMode) {
        case 'week':
          newDate.setDate(newDate.getDate() + 7);
          break;
        case 'month':
          newDate.setMonth(newDate.getMonth() + 1);
          break;
        case 'year':
          newDate.setFullYear(newDate.getFullYear() + 1);
          break;
        case 'custom':
          if (customDateRange) {
            const rangeDays = diffDays(customDateRange.start, customDateRange.end) + 1;
            newDate.setDate(newDate.getDate() + rangeDays);
          } else {
            newDate.setDate(newDate.getDate() + 7);
          }
          break;
      }
      return newDate;
    });
  }, [viewMode, customDateRange]);

  const goToCurrentPeriod = useCallback(() => {
    setAnchorDate(new Date());
  }, []);

  const handleShiftClick = useCallback((shift: Shift, task?: CrmTask | null) => {
    setSelectedShift(shift);
    setSelectedTask(task || null);
    setSidePanelOpen(true);
  }, []);

  const handleTaskClick = useCallback((task: CrmTask) => {
    setSelectedShift(null);
    setSelectedTask(task);
    setSidePanelOpen(true);
  }, []);

  const closeSidePanel = useCallback(() => {
    setSidePanelOpen(false);
    setSelectedShift(null);
    setSelectedTask(null);
  }, []);

  const scheduleBacklogTask = useCallback(async (
    taskId: number,
    teamId: number,
    startDate: string,
    taskTitle: string = 'Job',
    teamName: string = 'Team'
  ) => {
    console.log('[DROP DEBUG] scheduleBacklogTask called:', { taskId, teamId, startDate, taskTitle, teamName });
    pdebug('scheduleBacklogTask:', { taskId, teamId, startDate, taskTitle, teamName });

    // Validate that we have a valid teamId - NO FALLBACK to direct shift creation
    if (!teamId || teamId <= 0) {
      console.error('[DROP DEBUG] Invalid teamId:', teamId);
      pdebugError('Invalid teamId:', teamId);
      toast.error('Kein gültiges Team ausgewählt - bitte zuerst Team anlegen');
      return;
    }

    console.log('[DROP DEBUG] Calling PlantafelService.scheduleBacklogTask...');
    try {
      const createdShift = await PlantafelService.scheduleBacklogTask(taskId, teamId, startDate, 'FULL_DAY');
      console.log('[DROP DEBUG] PlantafelService.scheduleBacklogTask returned:', createdShift);
      pdebug('Schedule success, shift created:', createdShift);
      toast.success('Job eingeplant');

      // HOTFIX-2: Optimistic remove from backlog (instant UI feedback)
      setBacklogTasks(prev => {
        const updated = prev.filter(t => t.id !== taskId);
        pdebug('Optimistic backlog update:', prev.length, '->', updated.length);
        return updated;
      });

      // Then refresh to get the new shift and ensure sync
      pdebug('Refreshing plantafel data...');
      await loadData();
      pdebug('Refresh complete');
    } catch (err: any) {
      pdebugError('Failed to schedule task:', err);

      // Handle 409 Conflict - show modal instead of toast
      if (err?.response?.status === 409) {
        const conflictData = err?.response?.data?.conflicts || err?.response?.data?.detail?.conflicts || [];
        pdebug('409 Conflict detected, showing modal:', conflictData);

        // Parse conflicts into WorkAreaConflict format
        const conflicts: WorkAreaConflict[] = Array.isArray(conflictData)
          ? conflictData.map((c: any) => ({
              shift_id: c.shift_id || 0,
              task_title: c.task_title || c.title || 'Bestehender Job',
              start_time: c.start_time || c.start || '',
              end_time: c.end_time || c.end || '',
              overlap_minutes: c.overlap_minutes || c.overlap || 0,
            }))
          : [];

        setConflictState({
          isOpen: true,
          conflicts,
          pendingSchedule: {
            taskId,
            teamId,
            startDate,
            taskTitle,
            teamName,
          },
        });
        // Don't throw - modal will handle the flow
        return;
      } else if (err?.response?.status === 404) {
        // Endpoint not available - show clear error, no fallback
        toast.error('Schedule-Endpoint nicht verfügbar. Backend prüfen.');
      } else if (err?.response?.status === 400) {
        const detail = err?.response?.data?.detail || err?.response?.data?.message || 'Ungültige Anfrage';
        toast.error(`Fehler: ${detail}`);
      } else {
        toast.error('Fehler beim Erstellen des Jobs');
      }
      throw err;
    }
  }, [loadData]);

  const closeConflictModal = useCallback(() => {
    setConflictState({
      isOpen: false,
      conflicts: [],
      pendingSchedule: null,
    });
  }, []);

  const pushConflicts = useCallback(async (): Promise<MovedShift[]> => {
    const pending = conflictState.pendingSchedule;
    if (!pending) {
      throw new Error('No pending schedule');
    }

    pdebug('pushConflicts for:', pending);

    // First, try to resolve the conflicts
    // Note: This assumes there's a created shift ID we can use - may need to adjust
    // For now, we'll retry the schedule with a "force" flag if the backend supports it
    try {
      // Attempt to schedule again - backend should handle the push
      const createdShift = await PlantafelService.scheduleBacklogTask(
        pending.taskId,
        pending.teamId,
        pending.startDate,
        'FULL_DAY'
        // TODO: Add force_resolve parameter if backend supports it
      );

      pdebug('pushConflicts success:', createdShift);
      toast.success('Job erstellt (Konflikte verschoben)');

      closeConflictModal();
      await loadData();

      // Return moved shifts info if available
      return [];
    } catch (err: any) {
      pdebugError('pushConflicts failed:', err);
      throw err;
    }
  }, [conflictState.pendingSchedule, closeConflictModal, loadData]);

  const moveShift = useCallback(async (
    shiftId: number,
    dateStr: string,
    workAreaId: number
  ) => {
    try {
      await PlantafelService.moveShift(shiftId, dateStr, workAreaId);
      toast.success('Schicht verschoben');
      await loadData();
    } catch (err) {
      pdebugError('Failed to move shift:', err);
      toast.error('Fehler beim Verschieben der Schicht');
      throw err;
    }
  }, [loadData]);

  const moveShiftByMinutes = useCallback(async (
    shiftId: number,
    deltaMinutes: number,
    newWorkAreaId?: number
  ) => {
    const originalShifts = [...shifts];

    setShifts(prev => prev.map(s => {
      if (s.id === shiftId) {
        const newStart = new Date(new Date(s.start_time).getTime() + deltaMinutes * 60 * 1000);
        const newEnd = new Date(new Date(s.end_time).getTime() + deltaMinutes * 60 * 1000);
        return {
          ...s,
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
          work_area: newWorkAreaId !== undefined ? newWorkAreaId : s.work_area,
        };
      }
      return s;
    }));

    try {
      await PlantafelService.moveShiftByMinutes(shiftId, deltaMinutes, newWorkAreaId);
      toast.success('Schicht verschoben');
      await loadData();
    } catch (err) {
      pdebugError('Failed to move shift:', err);
      toast.error('Fehler beim Verschieben der Schicht');
      setShifts(originalShifts);
      throw err;
    }
  }, [shifts, loadData]);

  const moveShiftByDays = useCallback(async (
    shiftId: number,
    deltaDays: number,
    newWorkAreaId?: number
  ) => {
    return moveShiftByMinutes(shiftId, deltaDays * 24 * 60, newWorkAreaId);
  }, [moveShiftByMinutes]);

  const moveShiftToTime = useCallback(async (
    shiftId: number,
    newStartDateTime: string,
    newWorkAreaId?: number
  ) => {
    const originalShifts = [...shifts];
    const targetShift = shifts.find(s => s.id === shiftId);
    if (!targetShift) return;

    // Optimistic update
    const currentStart = new Date(targetShift.start_time);
    const currentEnd = new Date(targetShift.end_time);
    const durationMs = currentEnd.getTime() - currentStart.getTime();
    const newStart = new Date(newStartDateTime);
    const newEnd = new Date(newStart.getTime() + durationMs);

    setShifts(prev => prev.map(s => {
      if (s.id === shiftId) {
        return {
          ...s,
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
          work_area: newWorkAreaId !== undefined ? newWorkAreaId : s.work_area,
        };
      }
      return s;
    }));

    try {
      await PlantafelService.moveShiftToTime(shiftId, newStartDateTime, newWorkAreaId);
      toast.success('Schicht verschoben');
      await loadData();
    } catch (err) {
      pdebugError('Failed to move shift:', err);
      toast.error('Fehler beim Verschieben der Schicht');
      setShifts(originalShifts);
      throw err;
    }
  }, [shifts, loadData]);

  const resizeShift = useCallback(async (
    shiftId: number,
    newEndDateTime: string
  ) => {
    const originalShifts = [...shifts];
    const targetShift = shifts.find(s => s.id === shiftId);
    if (!targetShift) return;

    setShifts(prev => prev.map(s => {
      if (s.id === shiftId) {
        return { ...s, end_time: newEndDateTime };
      }
      return s;
    }));

    try {
      await PlantafelService.resizeShift(shiftId, newEndDateTime);
      toast.success('Schicht angepasst');
      await loadData();
    } catch (err) {
      pdebugError('Failed to resize shift:', err);
      toast.error('Fehler beim Ändern der Schicht');
      setShifts(originalShifts);
      throw err;
    }
  }, [shifts, loadData]);

  const deleteShift = useCallback(async (shiftId: number) => {
    try {
      await PlantafelService.deleteShift(shiftId);
      toast.success('Schicht gelöscht');
      closeSidePanel();
      await loadData();
    } catch (err) {
      pdebugError('Failed to delete shift:', err);
      toast.error('Fehler beim Löschen der Schicht');
      throw err;
    }
  }, [loadData, closeSidePanel]);

  const rescheduleShift = useCallback(async (
    shiftId: number,
    newTeamId: number,
    teamName?: string
  ) => {
    pdebug('rescheduleShift:', { shiftId, newTeamId, teamName });

    try {
      await PlantafelService.rescheduleShift(shiftId, newTeamId, 'MOVE_ONLY');
      toast.success(`Schicht zu ${teamName || 'Team'} verschoben`);
      await loadData();
    } catch (err: any) {
      pdebugError('Failed to reschedule shift:', err);

      // Handle 409 Conflict
      if (err?.response?.status === 409) {
        const detail = err?.response?.data?.detail || err?.response?.data?.message;
        const teamNameFromError = err?.response?.data?.work_area_team_name;
        toast.error(`Konflikt: ${detail || 'Team-Kapazität überschritten'}${teamNameFromError ? ` (${teamNameFromError})` : ''}`);
      } else if (err?.response?.status === 400) {
        const detail = err?.response?.data?.detail || err?.response?.data?.message || 'Ungültige Anfrage';
        toast.error(`Fehler: ${detail}`);
      } else {
        toast.error('Fehler beim Verschieben der Schicht');
      }
      throw err;
    }
  }, [loadData]);

  const getShiftsForCell = useCallback((dateStr: string, workAreaId: number): Shift[] => {
    return PlantafelService.getShiftsForCell(shifts, dateStr, workAreaId);
  }, [shifts]);

  // FE-HF-R18-3: Optimistic UI - update shift locally without waiting for refresh
  const updateShiftOptimistically = useCallback((shiftId: number, updater: (shift: Shift) => Shift) => {
    setShifts(prev => prev.map(s => s.id === shiftId ? updater(s) : s));
  }, []);

  return {
    loading,
    error,
    laneGroups,
    shifts,
    backlogTasks,
    backlogMeta,
    backlogPagination,
    loadMoreBacklogTasks,
    loadingMoreBacklog,
    weekStart, // alias for dateRange.rangeFrom (backward compat)
    days,
    settings,
    selectedShift,
    selectedTask,
    sidePanelOpen,
    showJobsOnly,
    setShowJobsOnly,
    setWeekStart: setAnchorDate, // alias for setAnchorDate (backward compat)
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
    refresh: loadData,
    handleShiftClick,
    handleTaskClick,
    closeSidePanel,
    scheduleBacklogTask,
    moveShift,
    moveShiftByDays,
    moveShiftByMinutes,
    moveShiftToTime,
    resizeShift,
    deleteShift,
    rescheduleShift,
    getShiftsForCell,
    hasTeams,
    conflictState,
    closeConflictModal,
    pushConflicts,
    showRegularShifts,
    setShowRegularShifts,
    updateShiftOptimistically,
    // FE-OPS-R21-4: View Mode Support
    viewMode,
    setViewMode,
    viewDays,
    goToPreviousPeriod,
    goToNextPeriod,
    goToCurrentPeriod,
    customDateRange,
    setCustomDateRange,
    // FE-OPS-R22-1: Anchor date for display
    anchorDate,
    // FE-OPS-R23-2/3: Set anchor date for navigation
    setAnchorDate,
  };
}
